import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { captureException } from "@sentry/react"

import GrowingTextArea from "shared/growing_text_area"

import styles from "./title.module.scss"
import { updateApplet } from "./api"

const Title = ({ applet, includeEditableTitle }) => {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedName, setSavedName] = useState(applet.name)
  const [name, setName] = useState(applet.name)
  const inputRef = useRef(null)
  const cancelOnBlurRef = useRef(false)
  const maxLength = 140

  const isValid = value => value.trim().length > 0 && value.length <= maxLength

  const cancel = () => {
    setName(savedName)
    setEditing(false)
  }

  const onSave = async () => {
    if (!isValid(name)) {
      cancel()
      return
    }

    setEditing(false)
    if (name === savedName) {
      return
    }

    // The editor stays closed until this save returns, so two saves never overlap.
    setSaving(true)
    try {
      const { errors } = await updateApplet({ appletId: applet.id, name })
      if (errors?.length) {
        setName(savedName)
      } else {
        setSavedName(name)
      }
    } catch (error) {
      setName(savedName)
      captureException(error)
    } finally {
      setSaving(false)
    }
  }

  const onLeave = () => {
    if (cancelOnBlurRef.current) {
      cancelOnBlurRef.current = false
      cancel()
    } else {
      onSave()
    }
  }

  // Both keys leave through blur, so the textarea hides itself the same way as on a click outside.
  const onKeyDown = e => {
    // During IME composition, Enter confirms the composed text and must not save the title.
    // Safari reports that keydown only through keyCode 229.
    if (e.isComposing || e.keyCode === 229) {
      return
    }

    if (e.key === "Escape") {
      cancelOnBlurRef.current = true
      e.target.blur()
    } else if (e.key === "Enter") {
      e.preventDefault()
      e.target.blur()
    }
  }

  const startEditMode = () => {
    if (!includeEditableTitle || saving) {
      return
    }

    setEditing(true)
    inputRef.current.focus(true) // unhide the textarea inline before focusing, so it's a direct user action
    inputRef.current.setCursorPosition(name.length)
    inputRef.current.selectionEnd = name.length
  }

  useEffect(() => {
    document.dispatchEvent(new Event("header_title_rendered"))
  })

  const canSave = isValid(name)

  return (
    <>
      {/* GrowingTextArea skips its onBlur for an empty value; blur does not bubble, so capture it here. */}
      <div className={classnames(styles["title-editor"], { [styles.editing]: editing })} onBlurCapture={onLeave}>
        <GrowingTextArea
          maxLength={maxLength}
          className="connection-title"
          ref={inputRef}
          value={name}
          onChange={value => setName(value)}
          onKeyDown={onKeyDown}
          name="name"
          hideAtStart
        />
      </div>
      <h1
        className={classnames("connection-title", styles["connection-title"], {
          dynamic: applet.config_type === "dynamic",
          [styles.editable]: includeEditableTitle,
          [styles.editing]: editing,
        })}
        onClick={startEditMode}
      >
        {name}
      </h1>
      <div className={styles["edit-details"]}>
        {editing ? (
          <span className={classnames("body-caption", styles["name-length"])}>
            {name.length}/{maxLength}
          </span>
        ) : (
          includeEditableTitle && <div />
        )}
        {editing ? (
          <button className="body-caption" disabled={!canSave} onClick={onSave}>
            Save
          </button>
        ) : (
          includeEditableTitle && (
            <button className="body-caption" disabled={saving} onClick={startEditMode}>
              Edit title
            </button>
          )
        )}
      </div>
    </>
  )
}

Title.propTypes = {
  applet: PropTypes.object.isRequired,
  includeEditableTitle: PropTypes.bool.isRequired,
}
export default Title
